import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import { fetchTournamentPage, useListScope } from '@/hooks/useTournamentList';
import { useTournamentSearchIndexOptions } from '@/hooks/useTournamentSearchIndex';
import type { TournamentSearchScope } from '@/hooks/useTournamentSearchScope';
import { SEARCH_DEBOUNCE_MS, SEARCH_INDEX_PAGE_SIZE } from '@/constants';
import { filterTournamentsBySearch } from '@/utils/tournamentSearchMatch';
import type { Tournament } from '@/types';

/** What the panel shows: rows, or one of the states instead of them. */
export type TournamentSearchView = 'list' | 'loading' | 'error' | 'empty';

export interface TournamentSearchResults {
  view: TournamentSearchView;
  /** The field has text (rows are matches, not the whole tab). */
  hasQuery: boolean;
  /** Rows: the loaded tab list for an empty field, else the matches. */
  items: Tournament[];
  /** Tournaments in the tab with its filters (once the first index page is in). */
  tabTotal?: number;
  /** Server matches of the current text, when more than `items` ("Show all results"). */
  moreTotal?: number;
  /** Empty field: the tab has more pages to load. */
  hasNextPage: boolean;
  /** The index is loading (a page or a background refetch) — `loadMore` waits. */
  isFetchingIndex: boolean;
  /** Load the next tab page (no-op while one is loading). */
  loadMore: () => void;
}

/**
 * Header search results in `scope`. Opening loads the tab index (pages of
 * `SEARCH_INDEX_PAGE_SIZE`); typing filters it locally on every keystroke. A
 * complete index is the answer by itself. Otherwise a debounced server search
 * (same endpoint, filters + `search`) runs too: its answer replaces the local
 * matches once it is in for the current text — same order, it only adds rows.
 */
export function useTournamentSearchResults(scope: TournamentSearchScope, draft: string): TournamentSearchResults {
  const { keyBase, ready } = useListScope(scope.source);
  const index = useInfiniteQuery(useTournamentSearchIndexOptions(scope));
  const trimmed = draft.trim();
  const hasQuery = trimmed !== '';

  // Offset pages can overlap when the list changes between loads — keep each tournament once.
  const indexItems = useMemo(() => {
    const seen = new Set<Tournament['id']>();
    return (index.data?.pages.flatMap((page) => page.items) ?? []).filter((tournament) => {
      if (seen.has(tournament.id)) return false;
      seen.add(tournament.id);
      return true;
    });
  }, [index.data]);
  const localMatches = useMemo(() => filterTournamentsBySearch(indexItems, trimmed), [indexItems, trimmed]);
  const complete = index.data !== undefined && !index.hasNextPage;

  const search = useDebounce(trimmed, SEARCH_DEBOUNCE_MS);
  const serverParams = {
    page: 1,
    limit: SEARCH_INDEX_PAGE_SIZE,
    search,
    status: scope.status,
    season: scope.season,
    role: scope.role,
  };
  const server = useQuery({
    queryKey: [...keyBase, 'search', serverParams],
    queryFn: ({ signal }) => fetchTournamentPage(scope.source, serverParams, signal),
    enabled: ready && search !== '' && !complete,
  });
  // The server state belongs to the text in the field (not to a debounced older one).
  const serverIsCurrent = hasQuery && !complete && search === trimmed;

  const { hasNextPage, isFetching, fetchNextPage } = index;
  const base = {
    hasQuery,
    tabTotal: index.data?.pages[0]?.meta.total,
    hasNextPage: !hasQuery && hasNextPage,
    isFetchingIndex: isFetching,
    loadMore: () => {
      if (hasNextPage && !isFetching) void fetchNextPage();
    },
  };

  if (!hasQuery) {
    if (indexItems.length > 0) return { ...base, view: 'list', items: indexItems };
    let view: TournamentSearchView = 'loading';
    if (index.data) view = 'empty';
    else if (index.isError) view = 'error';
    return { ...base, view, items: [] };
  }

  const answer = serverIsCurrent ? server.data : undefined;
  // Both lists are prefixes of the same ordered matches — the longer one wins.
  const items = answer && answer.items.length > localMatches.length ? answer.items : localMatches;
  if (items.length > 0) {
    const moreTotal = answer && answer.meta.total > items.length ? answer.meta.total : undefined;
    return { ...base, view: 'list', items, moreTotal };
  }
  let view: TournamentSearchView = 'loading';
  if (complete || answer) view = 'empty';
  else if (serverIsCurrent && server.isError) view = 'error';
  return { ...base, view, items };
}
